import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { User } from './models/user';

@Injectable({
  providedIn: 'root'
})
export class ClientSelectionGuard implements CanActivate {
  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  canActivate(_next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const targetUrl = new URL(state.url, globalThis.location.origin);
    const clientSelectionUrl = new URL(this.authService.config.clientSelectionRoute, globalThis.location.origin);
    const isClientSelectionUrl = targetUrl.pathname === clientSelectionUrl.pathname;

    // if manageClientIdSelection is not set, then client selection is handled entirely outside the library
    if (!this.authService.config.manageClientIdSelection) {
      return isClientSelectionUrl ? of(this.router.parseUrl('/')) : of(true);
    }

    if (isClientSelectionUrl) return of(true);

    return this.authService.currentUser$.pipe(
      filter(user => user.hasValidAccessToken),
      switchMap(user =>
        forkJoin({
          isSignedInClientValid: this.authService.isSignedInClientValid$.pipe(take(1)),
          signedInClientId: this.authService.signedInClientId$.pipe(take(1))
        }).pipe(map(clientIdInfo => ({ ...clientIdInfo, user })))
      ),
      map(({ isSignedInClientValid, signedInClientId, user }) => {
        if (
          this.validateMriClientId(signedInClientId, isSignedInClientValid, user) ||
          (!signedInClientId && this.authService.signInClient(user))
        ) {
          return this.stripMriClientIdQueryParam(targetUrl);
        }

        const mriClientId = this.authService.getMriClientIdFromUrl(targetUrl);
        if (mriClientId) {
          this.authService.setMriClientIdOnUrl(clientSelectionUrl, mriClientId);
        }
        const lastValidatedClientId = this.getLastValidatedClientId();
        if (lastValidatedClientId) {
          clientSelectionUrl.searchParams.set('lastValidatedClientId', lastValidatedClientId);
        }
        clientSelectionUrl.searchParams.set('redirectUrl', state.url);
        return this.routeUrlTo(clientSelectionUrl);
      }),
      take(1)
    );
  }

  private getLastValidatedClientId() {
    const regex = new RegExp(`(?:^|; )${this.authService.config.validatedClientCookieName}=([^;]*)`);
    const match = regex.exec(this.document.cookie);
    return match?.[1] ? decodeURIComponent(match[1]) : undefined;
  }

  private routeUrlTo(url: URL) {
    return this.router.parseUrl(`${url.pathname}?${url.searchParams.toString()}`);
  }

  private stripMriClientIdQueryParam(targetUrl: URL) {
    if (!this.authService.hasMriClientIdInUrl(targetUrl)) return true;

    this.authService.deleteMriClientIdFromUrl(targetUrl);
    return this.routeUrlTo(targetUrl);
  }

  private validateMriClientId(signedInClientId: string | undefined, isSignedInClientValid: boolean, user: User) {
    if (isSignedInClientValid) return true;

    return signedInClientId != null && this.authService.validateSignedInClientId(user, signedInClientId);
  }
}
